import { gsap } from '../motion';
import { DEBUG_ENABLED, metrics, type MetricsSnapshot } from './metrics';

/**
 * A read-only handle for automated audits, installed only under `?debug=1`.
 *
 * Two of the performance claims in the plan cannot be checked from the outside
 * without it. "The node count does not grow" is visible in the DOM, but "no
 * unmounted view keeps a tween or a callback alive" is not: an orphaned tween
 * leaves nothing to query — it just keeps ticking against a detached node and
 * costs a little more of every frame from then on. The only place that fact
 * exists is GSAP's global timeline.
 *
 * Deliberately behind the same flag as the overlay, and deliberately read-only:
 * it reports numbers, it does not offer a way to change them.
 */

export interface DebugInspector {
  /** Tweens and timelines currently attached to the global ticker. */
  activeTweens(): number;
  /** Everything the overlay is showing, without scraping its DOM. */
  metrics(): MetricsSnapshot;
}

declare global {
  interface Window {
    __2k48?: DebugInspector;
  }
}

export function installInspector(): void {
  if (!DEBUG_ENABLED || typeof window === 'undefined') return;

  window.__2k48 = {
    // `true` recurses into timelines, so a tween parked inside a leaked
    // timeline is counted rather than hidden behind its parent.
    activeTweens: () => gsap.globalTimeline.getChildren(true, true, true).length,
    metrics: () => metrics.read(),
  };
}
